import React from "react";
import { MDBContainer } from "mdb-react-ui-kit";
import Button from "./Button";
import "./page-layout.css";

/**
 * Standard page shell used across list/dashboard pages.
 * Padding comes from app-page-shell in App.js (24px).
 */
export function PageLayout({
  rootClass,
  title,
  titleExtra,
  children,
  containerClassName = "",
}) {
  const rootCls = rootClass?.endsWith("-page-root")
    ? rootClass
    : `${rootClass}-page-root`;

  return (
    <div className={`${rootCls} oh-page`}>
      <MDBContainer fluid className={`content-area flex-grow-1 ${containerClassName}`}>
        {title ? (
          <div className={`oh-page-header${titleExtra ? " oh-page-header--split" : ""}`}>
            <h1 className="page-title-header mb-0">{title}</h1>
            {titleExtra || null}
          </div>
        ) : null}
        {children}
      </MDBContainer>
    </div>
  );
}

export function PageSection({ children, className = "oh-page-section" }) {
  return <div className={className}>{children}</div>;
}

/**
 * Add / edit form pages — title above a single card, fixed max width.
 */
export function PageFormLayout({ rootClass, title, children }) {
  const rootCls = rootClass?.endsWith("-page-root")
    ? rootClass
    : `${rootClass}-page-root`;

  return (
    <div className={`${rootCls} oh-page`}>
      <MDBContainer fluid className="content-area flex-grow-1">
        {title ? (
          <div className="oh-page-header">
            <h1 className="page-title-header mb-0">{title}</h1>
          </div>
        ) : null}
        <div className="oh-form-page">
          <div className="card oh-form-card shadow-sm">
            <div className="card-body">{children}</div>
          </div>
        </div>
      </MDBContainer>
    </div>
  );
}

/** Section heading inside a form card */
export function FormSection({ title, children, showDivider = false }) {
  return (
    <section
      className={`oh-form-section${showDivider ? " oh-form-section--divider" : ""}`}
    >
      {title ? <h5 className="oh-form-section__title">{title}</h5> : null}
      {children}
    </section>
  );
}

/** Standard cancel / save footer for add-edit forms */
export function FormActions({
  onCancel,
  onSave,
  cancelLabel = "Cancel",
  saveLabel = "Save",
  saving = false,
  savingLabel,
  saveDisabled = false,
  cancelDisabled = false,
  isRtl = false,
  type = "button",
}) {
  const cancelBtn = (
    <Button
      color="secondary"
      size="md"
      type="button"
      onClick={onCancel}
      disabled={cancelDisabled || saving}
    >
      {cancelLabel}
    </Button>
  );

  const saveBtn = (
    <Button
      color="primary"
      size="md"
      type={type}
      onClick={onSave}
      disabled={saveDisabled || saving}
    >
      {saving ? (
        <>
          <span
            className="spinner-border spinner-border-sm me-2"
            role="status"
            aria-hidden="true"
          />
          {savingLabel || saveLabel}
        </>
      ) : (
        saveLabel
      )}
    </Button>
  );

  return (
    <div className="oh-form-actions">
      {isRtl ? (
        <>
          {saveBtn}
          {cancelBtn}
        </>
      ) : (
        <>
          {cancelBtn}
          {saveBtn}
        </>
      )}
    </div>
  );
}

export default PageLayout;
